import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'

import { ACTIVITY_DAYS, DASHBOARD_STALE_TIME } from './constants'

export type MetricPoint = components['schemas']['MetricPoint']
type AdminMetricsDto = components['schemas']['AdminMetricsDto']

export interface MetricSeriesPoint {
  date: string
  value: number
}

export interface AdminMetrics {
  creditedUsd: MetricSeriesPoint[]
  newUsers: MetricSeriesPoint[]
  newVms: MetricPoint[]
  creditedTotalCents: number
  newUsersTotal: number
}

function toSeries(
  points: MetricPoint[],
  transform: (value: number) => number = (value) => value,
): MetricSeriesPoint[] {
  return points.map((point) => ({
    date: format(parseISO(point.day), 'MMM d'),
    value: transform(point.value),
  }))
}

function sum(points: MetricPoint[]): number {
  return points.reduce((total, point) => total + point.value, 0)
}

export function buildAdminMetrics(dto: AdminMetricsDto): AdminMetrics {
  const { creditedAmountCents, newUsers, newVms } = dto
  return {
    creditedUsd: toSeries(creditedAmountCents, (cents) => cents / 100),
    newUsers: toSeries(newUsers),
    newVms,
    creditedTotalCents: sum(creditedAmountCents),
    newUsersTotal: sum(newUsers),
  }
}

async function fetchAdminMetrics(): Promise<AdminMetrics> {
  const result = await client.GET('/v1/admin/metrics', {
    params: { query: { days: ACTIVITY_DAYS } },
  })
  return buildAdminMetrics(
    unwrapApiData(result, 'Failed to fetch dashboard metrics'),
  )
}

export function useAdminMetrics() {
  return useQuery({
    queryKey: ['dashboard', 'adminMetrics', ACTIVITY_DAYS],
    queryFn: fetchAdminMetrics,
    staleTime: DASHBOARD_STALE_TIME,
  })
}
